import { useEffect, useEffectEvent, useMemo, useState } from 'react';
import type { AgentTerminalDisplay, TerminalSession } from '@/components/desktop-panel-primitives';
import type { StateSetter } from '@/lib/desktop-shell/store';
import { useWorkspaceName } from '@/lib/desktop-shell/use-workspace-name';
import { openPanelTab, type PanelTabs, panelTabsOf } from '@/lib/side-panel/panel-tabs';
import type { SidePanelView } from '@/lib/side-panel/side-panel-items';

const NO_RETAINED_TABS: ReadonlySet<string> = new Set();

/**
 * The terminals of the side panel, one per tab of the terminal view: an agent's terminal the
 * user opened, or a shell of the workspace. A terminal tab shown without one starts a shell.
 */
export function useTerminalTabs({
  agentTerminals,
  hidden,
  onAgentTerminalRequestConsumed,
  onTerminalActionConsumed,
  panelView,
  retainedTabIds = NO_RETAINED_TABS,
  selectedAgentTerminalId,
  setTabs,
  tabs,
  terminalAction,
  workspace,
}: {
  agentTerminals: readonly AgentTerminalDisplay[];
  hidden: boolean;
  onAgentTerminalRequestConsumed: () => void;
  onTerminalActionConsumed: () => void;
  /** The terminal view, once the workbench declaring it has loaded. */
  panelView: SidePanelView | undefined;
  /** Tabs open in other conversations' panels: their terminals are kept for when they show again. */
  retainedTabIds?: ReadonlySet<string>;
  selectedAgentTerminalId: string | null;
  setTabs: StateSetter<PanelTabs>;
  tabs: PanelTabs;
  terminalAction: {
    id: string;
    name: string;
    command: string;
    env: Record<string, string>;
  } | null;
  workspace: string | null;
}) {
  const workspaceName = useWorkspaceName(workspace);
  const [shellSessions, setShellSessions] = useState<readonly TerminalSession[]>([]);
  const [agentTerminalIds, setAgentTerminalIds] = useState<ReadonlySet<string>>(() => new Set());
  const viewTabs = useMemo(
    () => (panelView === undefined ? [] : panelTabsOf(tabs, panelView)),
    [panelView, tabs],
  );

  function shellSession(id: string): TerminalSession {
    return { id, name: `Terminal · ${workspaceName ?? 'Shell'}` };
  }

  const missingShellIds = viewTabs
    .filter(
      (tab) =>
        !agentTerminalIds.has(tab.id) && !shellSessions.some((session) => session.id === tab.id),
    )
    .map((tab) => tab.id)
    .join('\n');
  // Named as the workspace is named when they open.
  const addShellSessions = useEffectEvent((ids: readonly string[]) =>
    setShellSessions((current) => [
      ...current,
      ...ids
        .filter((id) => !current.some((session) => session.id === id))
        .map((id) => shellSession(id)),
    ]),
  );
  useEffect(() => {
    if (missingShellIds.length === 0) return;
    addShellSessions(missingShellIds.split('\n'));
  }, [missingShellIds]);

  // A closed tab ends its terminal.
  const openIds = viewTabs.map((tab) => tab.id).join('\n');
  useEffect(() => {
    if (panelView === undefined) return;
    const open = new Set(openIds.split('\n'));
    const kept = (id: string) => open.has(id) || retainedTabIds.has(id);
    setShellSessions((current) => {
      const left = current.filter((session) => kept(session.id));
      return left.length === current.length ? current : left;
    });
    setAgentTerminalIds((current) => {
      const left = [...current].filter(kept);
      return left.length === current.size ? current : new Set(left);
    });
  }, [openIds, panelView, retainedTabIds]);

  const openAgentTerminals = useMemo(
    () =>
      viewTabs.flatMap((tab) =>
        agentTerminalIds.has(tab.id)
          ? agentTerminals.filter((terminal) => terminal.id === tab.id)
          : [],
      ),
    [agentTerminalIds, agentTerminals, viewTabs],
  );
  const terminalSessions = useMemo(
    () => viewTabs.flatMap((tab) => shellSessions.filter((session) => session.id === tab.id)),
    [shellSessions, viewTabs],
  );
  const terminalTabs = useMemo(
    () =>
      viewTabs.flatMap((tab) => {
        const agentTerminal = openAgentTerminals.find((terminal) => terminal.id === tab.id);
        if (agentTerminal !== undefined) return [{ id: tab.id, name: agentTerminal.command }];
        return terminalSessions.filter((session) => session.id === tab.id);
      }),
    [openAgentTerminals, terminalSessions, viewTabs],
  );

  function addTerminalTab(): void {
    if (panelView === undefined) return;
    const session = shellSession(`terminal-${crypto.randomUUID()}`);
    setShellSessions((current) => [...current, session]);
    setTabs((current) => openPanelTab(current, { id: session.id, view: panelView }));
  }

  function renameTerminalSession(id: string, name: string): void {
    setShellSessions((current) =>
      current.map((session) => (session.id === id ? { ...session, name } : session)),
    );
  }

  useEffect(() => {
    if (hidden || panelView === undefined || selectedAgentTerminalId === null) return;
    if (!agentTerminals.some((terminal) => terminal.id === selectedAgentTerminalId)) return;
    setAgentTerminalIds((current) =>
      current.has(selectedAgentTerminalId)
        ? current
        : new Set([...current, selectedAgentTerminalId]),
    );
    setTabs((current) => openPanelTab(current, { id: selectedAgentTerminalId, view: panelView }));
    onAgentTerminalRequestConsumed();
  }, [
    agentTerminals,
    hidden,
    onAgentTerminalRequestConsumed,
    panelView,
    selectedAgentTerminalId,
    setTabs,
  ]);

  useEffect(() => {
    if (terminalAction === null || panelView === undefined) return;
    const session: TerminalSession = {
      id: `terminal-action-${terminalAction.id}`,
      name: terminalAction.name,
      initialCommand: terminalAction.command,
      env: terminalAction.env,
    };
    setShellSessions((current) =>
      current.some((candidate) => candidate.id === session.id) ? current : [...current, session],
    );
    setTabs((current) => openPanelTab(current, { id: session.id, view: panelView }));
    onTerminalActionConsumed();
  }, [onTerminalActionConsumed, panelView, setTabs, terminalAction]);

  return {
    addTerminalTab,
    openAgentTerminals,
    renameTerminalSession,
    terminalSessions,
    terminalTabs,
  };
}
